type FarmLike = {
  metadata?: Record<string, unknown>
}

export type FarmProcessingSiteOption = {
  id: string
  name?: string
  label: string
}

export type FarmProcessingSiteMetadataEntry = string | {
  id: string
  name: string
}

const normalizeOptionalText = (value: unknown): string | null => {
  if (typeof value !== 'string') return null
  const normalized = value.trim()
  return normalized.length > 0 ? normalized : null
}

export const listFarmProcessingSites = (farm?: FarmLike): FarmProcessingSiteOption[] => {
  if (!farm?.metadata || typeof farm.metadata !== 'object') {
    return []
  }

  const rawProcessingSites = farm.metadata.processingSites
  if (!Array.isArray(rawProcessingSites)) {
    return []
  }

  const seen = new Set<string>()
  const options: FarmProcessingSiteOption[] = []

  rawProcessingSites.forEach((entry) => {
    let id: string | null = null
    let name: string | null = null

    if (typeof entry === 'string') {
      id = normalizeOptionalText(entry)
    } else if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
      const record = entry as Record<string, unknown>
      id = normalizeOptionalText(record.id)
      name = normalizeOptionalText(record.name)
    }

    if (!id || seen.has(id)) {
      return
    }

    seen.add(id)
    options.push({
      id,
      name: name ?? undefined,
      label: name ?? id
    })
  })

  return options
}

export const formatFarmProcessingSitesDraft = (farm?: FarmLike): string => (
  listFarmProcessingSites(farm)
    .map((site) => site.name ? `${site.id} | ${site.name}` : site.id)
    .join('\n')
)

export const parseFarmProcessingSitesDraft = (value: string): {
  metadataValue: FarmProcessingSiteMetadataEntry[]
  sites: FarmProcessingSiteOption[]
  invalidLines: string[]
} => {
  const metadataValue: FarmProcessingSiteMetadataEntry[] = []
  const sites: FarmProcessingSiteOption[] = []
  const invalidLines: string[] = []
  const seen = new Set<string>()

  value.split(/\r?\n/).forEach((line) => {
    const rawLine = line.trim()
    if (!rawLine) return

    const [rawId, ...rawNameParts] = rawLine.split('|')
    const id = normalizeOptionalText(rawId)
    const name = normalizeOptionalText(rawNameParts.join('|'))

    if (!id) {
      invalidLines.push(rawLine)
      return
    }

    if (seen.has(id)) {
      return
    }

    seen.add(id)
    metadataValue.push(name ? { id, name } : id)
    sites.push({
      id,
      name: name ?? undefined,
      label: name ?? id
    })
  })

  return {
    metadataValue,
    sites,
    invalidLines
  }
}